import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import StoreLayout from './components/layout/StoreLayout';
import HomePage from './pages/home/HomePage';
import ProductsPage from './pages/products/ProductsPage';
import DealsPage from './pages/deals/DealsPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import AccountPage from './pages/account/AccountPage';
import TrackOrderPage from './pages/track/TrackOrderPage';
import WishlistPage from './pages/wishlist/WishlistPage';
import ThankYouPage from './pages/thankyou/ThankYouPage';
import CategoryPage from './pages/category/CategoryPage';
import CategoriesPage from './pages/categories/CategoriesPage';
import LoginPage from './pages/auth/LoginPage';
import GoogleCallback from './pages/auth/GoogleCallback';
import BlogPage from './pages/blog/BlogPage';
import BlogDetailPage from './pages/blog/BlogDetailPage';
import {
  FaqPage, ContactPage, AboutPage,
  PrivacyPolicyPage, TermsPage, ShippingPolicyPage, RefundPolicyPage, CookiePolicyPage,
} from './pages/static/StaticPages';
import CookieBanner from './components/common/CookieBanner';
import TagInjector from './components/common/TagInjector';
import PageViewTracker from './components/common/PageViewTracker';
import RouteSeo from './components/common/RouteSeo';
import { getAnchor, clearAnchor, shouldForceTop } from './utils/scrollMemory';

// Scroll to top on new navigations, but restore the previous scroll position
// when the user presses Back/Forward (so returning from a product lands them
// where they were, not at the header).
function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const positions = useRef(new Map());
  const restoring = useRef(false);

  // Continuously remember where we are on the current history entry.
  // Skip while we're actively restoring, otherwise the browser clamping the
  // scroll to a still-loading (short) page would overwrite the real position.
  useEffect(() => {
    const key = location.key;
    const onScroll = () => { if (!restoring.current) positions.current.set(key, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key]);

  // When the user navigates away by clicking something that ISN'T a product card
  // (a category tile, banner, "View All" link), clear this entry's card anchor so
  // a stale one from an earlier product click doesn't hijack the Back scroll.
  // Product cards keep their own anchor (they carry data-pcard), so we skip those.
  useEffect(() => {
    const key = location.key;
    const onClickCapture = (e) => {
      if (e.target.closest('button')) return; // in-card buttons (wishlist/cart) don't navigate
      const link = e.target.closest('a[href], [data-pcard]');
      if (!link || link.hasAttribute('data-pcard')) return; // cards set their own anchor
      const href = link.getAttribute('href');
      if (href && href.startsWith('/')) clearAnchor(key); // internal non-card navigation
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [location.key]);

  // useLayoutEffect so that when the page is hydrated from cache (Back/Forward),
  // the first scroll to the clicked card happens BEFORE the browser paints —
  // the user never sees the top/header, it just appears already on the card.
  useLayoutEffect(() => {
    const anchor = getAnchor(location.key); // { id, top } of the clicked card
    const saved = positions.current.get(location.key);
    // Pagination entries: ignore the saved (deep) position and land at the top.
    // A product-card anchor still wins, so Back from a product to a paginated
    // listing keeps returning to the exact card.
    const forceTop = !anchor && shouldForceTop(location.key);

    // The same product can be in several rows, so there may be multiple cards
    // with this id. Pick the one whose current position is closest to where the
    // user actually clicked, so we land in the right section (not the first row).
    const findAnchorEl = () => {
      if (!anchor) return null;
      const els = document.querySelectorAll(`[data-pcard="${CSS.escape(anchor.id)}"]`);
      if (els.length === 0) return null;
      if (els.length === 1 || anchor.top == null) return els[0];
      let best = els[0], bestDist = Infinity;
      els.forEach((el) => {
        const top = el.getBoundingClientRect().top + window.scrollY;
        const dist = Math.abs(top - anchor.top);
        if (dist < bestDist) { bestDist = dist; best = el; }
      });
      return best;
    };

    if (navType === 'POP' && !forceTop && (anchor || saved != null)) {
      // Returning via Back/Forward. The page's content (product rows, lazy
      // sections, images) loads in over time and keeps changing its height, so
      // a one-shot pixel scroll lands on the footer. Instead we keep pulling the
      // clicked card back into view every frame until the layout settles — this
      // tracks the real element no matter how the height shifts. We bail out the
      // moment the user scrolls themselves so we never fight them.
      restoring.current = true;
      let frame;
      let userScrolled = false;
      const start = performance.now();

      const onUserScroll = () => { userScrolled = true; };
      // Real user intent only — programmatic scrollTo/scrollIntoView don't fire these.
      window.addEventListener('wheel', onUserScroll, { passive: true });
      window.addEventListener('touchmove', onUserScroll, { passive: true });
      window.addEventListener('keydown', onUserScroll);

      const stop = () => {
        restoring.current = false;
        cancelAnimationFrame(frame);
        window.removeEventListener('wheel', onUserScroll);
        window.removeEventListener('touchmove', onUserScroll);
        window.removeEventListener('keydown', onUserScroll);
      };

      let lastHeight = null, heightStableSince = null;
      const restore = () => {
        if (userScrolled) { stop(); return; }
        const now = performance.now();
        const el = findAnchorEl();
        if (el) {
          // Found the clicked card. Keep it centered every frame and follow it
          // until the page height has been stable for a beat — i.e. images and
          // any remaining async rows have finished loading, so the card won't
          // drift after we release.
          el.scrollIntoView({ block: 'center' });
          const h = document.documentElement.scrollHeight;
          if (lastHeight != null && h === lastHeight) {
            if (heightStableSince == null) heightStableSince = now;
            if (now - heightStableSince > 500) { stop(); return; }
          } else {
            heightStableSince = null; // page still growing — keep following
          }
          lastHeight = h;
          if (now - start > 5000) { stop(); return; } // hard safety cap
        } else if (anchor) {
          // Card not rendered yet (page still fetching) — wait at the top rather
          // than clamping to the footer. Keep waiting up to 10s for slow loads.
          window.scrollTo(0, 0);
          if (now - start > 10000) { stop(); return; }
        } else if (saved != null) {
          // No anchor (non-product page) — plain pixel restore.
          window.scrollTo(0, saved);
          if (now - start > 3000) { stop(); return; }
        } else { stop(); return; }
        frame = requestAnimationFrame(restore);
      };
      // Run once synchronously (pre-paint) for the seamless cached case, then it
      // keeps itself scheduled to track any late layout shifts.
      restore();
      return stop;
    }

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, [location.key, navType]);

  return null;
}

function ImageProtection() {
  useEffect(() => {
    const block = (e) => { if (e.target.tagName === 'IMG') e.preventDefault(); };
    document.addEventListener('contextmenu', block);
    document.addEventListener('dragstart', block);
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('dragstart', block);
    };
  }, []);
  return null;
}

const ComingSoon = ({ title }) => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8">
    <div className="w-16 h-16 bg-brand-yellow-light rounded-2xl flex items-center justify-center mb-4 text-3xl">🚧</div>
    <h2 className="text-xl font-bold text-brand-dark">{title}</h2>
    <p className="text-gray-400 text-sm mt-1">Coming soon</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route element={<StoreLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/deals" element={<DealsPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/product/:slug" element={<ProductDetailPage />} />
        <Route path="/product/variant/:variantSlug" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/thankyou" element={<ThankYouPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/orders" element={<AccountPage />} />
        <Route path="/track-order" element={<TrackOrderPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <ImageProtection />
      <TagInjector />
      <PageViewTracker />
      <RouteSeo />
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
            <AppRoutes />
            <CookieBanner />
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
